/**
 * ─────────────────────────────────────────────────────────────────────────────
 * AgroCast — useTheme Hook
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The primary interface for consuming theme in components.
 *
 * Usage:
 *   const { resolvedTheme, toggleTheme, isDark } = useTheme()
 *   const { glassClasses } = useTheme()
 *
 * Always import from '@theme' barrel — never import ThemeContext directly.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useContext } from 'react'
import { ThemeContext } from './themeContext'
import { getGlassClasses, getCssVar } from './themeUtils'
import { THEMES } from './tokens'

/**
 * useTheme — full theme API.
 * Returns state, actions, derived helpers, and utility functions.
 */
export function useTheme() {
  const ctx = useContext(ThemeContext)

  if (!ctx) {
    throw new Error('useTheme must be used within a <ThemeProvider>.')
  }

  return {
    // ── State ────────────────────────────────────────────────────────────────
    /** User's stored preference: 'light' | 'dark' | 'system' */
    preference: ctx.preference,

    /** Actual resolved theme applied to DOM: 'light' | 'dark' */
    resolvedTheme: ctx.resolvedTheme,

    /** True while theme transition animation is running */
    isTransitioning: ctx.isTransitioning,

    // ── Derived booleans ─────────────────────────────────────────────────────
    isDark:   ctx.isDark,
    isLight:  ctx.isLight,
    isSystem: ctx.isSystem,

    // ── Actions ──────────────────────────────────────────────────────────────
    /** Set theme explicitly: 'light' | 'dark' | 'system' */
    setTheme: ctx.setTheme,

    /** Toggle between light ↔ dark */
    toggleTheme: ctx.toggleTheme,

    /** Reset to OS preference */
    resetToSystem: ctx.resetToSystem,

    // ── Utility helpers ──────────────────────────────────────────────────────

    /**
     * Returns glassmorphism Tailwind classes for the current theme.
     *
     * Light: backdrop-blur-xl bg-white/60 border-white/20 shadow-xl
     * Dark:  backdrop-blur-xl bg-slate-900/60 border-slate-700/40 shadow-xl
     */
    glassClasses: getGlassClasses(ctx.resolvedTheme),

    /**
     * Reads a CSS variable value at runtime.
     * Useful for canvas rendering, SVG fills, Chart.js colors, etc.
     *
     * @param {string} token — e.g. 'primary', 'text-base', 'bg-surface'
     * @returns {string} computed CSS value
     */
    cssVar: getCssVar,

    /** All available theme names */
    themes: THEMES,
  }
}

/**
 * useIsDark — lightweight hook when you only need isDark.
 * Prevents unnecessary re-renders from the full context shape.
 */
export function useIsDark() {
  const { isDark } = useTheme()
  return isDark
}

/**
 * useResolvedTheme — lightweight hook when you only need resolvedTheme.
 */
export function useResolvedTheme() {
  const { resolvedTheme } = useTheme()
  return resolvedTheme
}
